// Der Admin bearbeitet bestellte Stammbäume direkt vom Server – es wird nichts im
// Browser zwischengespeichert. Die Funktionen entsprechen useTreeData.js; hier wird
// customColors aber korrekt mitgegeben (…cur).
// useAdminTreeData.js – wie useTreeData, aber OHNE localStorage (Admin-Modus)
import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { buildRelationships } from '../utils/treeRules';

export function useAdminTreeData() {
  const [data, setData] = useState({ persons: [], relationships: [], customColors: {} });
  const dataRef = useRef(data);

  const persist = useCallback((newData) => {
    dataRef.current = newData;
    setData(newData);
  }, []);

  // Vom Server geladene Daten übernehmen (fehlende Teile werden leer ergänzt).
  const loadData = useCallback((d) => {
    const normalized = {
      persons:       d.persons       || [],
      relationships: d.relationships || [],
      customColors:  d.customColors  || {},
      extension:     d.extension     || null,   // Erweiterung (siehe utils/extension.js)
    };
    dataRef.current = normalized;
    setData(normalized);
  }, []);

  const addPerson = useCallback((personData, relationshipsInput) => {
    const rels = Array.isArray(relationshipsInput) ? relationshipsInput : (relationshipsInput ? [relationshipsInput] : []);
    const newPerson = {
      id:         uuidv4(),
      name:       personData.name       || '',
      maidenName: personData.maidenName || '',
      birthDate:  personData.birthDate  || '',
      gender:     personData.gender     || 'unknown',
      photo:      personData.photo      || null,
      photoEdit:  personData.photoEdit  || null,
      photoRetouch: personData.photoRetouch || null,
      firstName:  personData.firstName  ?? undefined,
      lastName:   personData.lastName   ?? undefined,
    };
    const cur = dataRef.current;
    const newRels = buildRelationships(newPerson.id, rels, cur.relationships);
    persist({
      ...cur,
      persons:       [...cur.persons, newPerson],
      relationships: [...cur.relationships, ...newRels],
    });
    return newPerson.id;
  }, [persist]);

  const updatePerson = useCallback((id, personData, relationshipsInput) => {
    const cur = dataRef.current;
    const updatedPersons = cur.persons.map(p => p.id === id ? { ...p, ...personData } : p);
    let updatedRels = cur.relationships;
    if (Array.isArray(relationshipsInput)) {
      updatedRels = updatedRels.filter(r => r.from !== id && r.to !== id);
      updatedRels = [...updatedRels, ...buildRelationships(id, relationshipsInput, updatedRels)];
    }
    persist({ ...cur, persons: updatedPersons, relationships: updatedRels });
  }, [persist]);

  const deletePerson = useCallback((id) => {
    const cur = dataRef.current;
    persist({
      ...cur,
      persons:       cur.persons.filter(p => p.id !== id),
      relationships: cur.relationships.filter(r => r.from !== id && r.to !== id),
    });
  }, [persist]);

  const setCustomColor = useCallback((personId, colorIndex, gen1Siblings) => {
    const cur = dataRef.current;
    const prev = cur.customColors || {};
    const newCC = { ...prev };
    const getEff = (id) => {
      if (newCC[id] !== undefined) return newCC[id];
      const idx = gen1Siblings ? gen1Siblings.indexOf(id) : -1;
      return idx >= 0 ? idx % 9 : -1;
    };
    if (gen1Siblings) {
      const other = gen1Siblings.find(id => id !== personId && getEff(id) === colorIndex);
      if (other) newCC[other] = getEff(personId);
    }
    newCC[personId] = colorIndex;
    persist({ ...cur, customColors: newCC });
  }, [persist]);

  return {
    persons:       data.persons,
    relationships: data.relationships,
    customColors:  data.customColors || {},
    extension:     data.extension || null,
    addPerson,
    updatePerson,
    deletePerson,
    setCustomColor,
    loadData,
    // Aktueller Stand zum Speichern auf dem Server.
    getSnapshot: () => dataRef.current,
  };
}
